import { Link } from "@tanstack/react-router"
import { useTheme } from "../components/theme"
import { Sun, Moon } from "lucide-react"
import { Button } from "../components/ui"

const LAST_UPDATED = "June 28, 2025"

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  )
}

export default function PrivacyPage() {
  const { theme, toggle } = useTheme()

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b bg-card/80 px-4 py-3 shadow-sm backdrop-blur sm:px-6">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/landing" className="flex items-center gap-2">
            <img src="/logo.svg" alt="Taskoru" className="h-9 w-9 rounded-xl shadow-sm" />
            <span className="text-lg font-semibold tracking-tight">Taskoru</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="ghost" size="sm" onClick={toggle} title={theme === "dark" ? "Light mode" : "Dark mode"}>
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Link to="/login" className="hidden text-sm font-medium text-muted-foreground hover:text-foreground sm:inline">Log in</Link>
            <Link to="/register"><Button size="sm">Get started</Button></Link>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-12 sm:px-6">
        <div className="mb-10">
          <h1 className="mb-2 text-3xl font-bold tracking-tight">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>
        </div>

        <div className="space-y-10">
          <Section title="The short version">
            <p>We collect only what we need to run Taskoru. We don't sell your data, we don't share it with advertisers, and we don't do anything with it that would surprise you. Read on for the details.</p>
          </Section>

          <Section title="What we collect">
            <p><strong className="text-foreground">Account information.</strong> When you sign up we store your name, email address, and a hashed password (or an OAuth token if you use Google). That's it.</p>
            <p><strong className="text-foreground">Your content.</strong> Anything you create inside Taskoru — workspaces, boards, cards, comments, attachments — is stored in our database so you can access it. We don't read it.</p>
            <p><strong className="text-foreground">Usage data.</strong> We log basic server activity (request paths, error rates) to keep the service running. We don't track you across the web or build behavioural profiles.</p>
            <p><strong className="text-foreground">Payment information.</strong> Billing is handled entirely by Stripe. We never see or store your card number. We only receive a customer ID and subscription status from Stripe.</p>
          </Section>

          <Section title="How we use it">
            <p>To run the service — authenticate you, save your work, send notifications you've asked for, and process payments.</p>
            <p>To contact you about your account — things like password resets, invoices, or important service changes. We don't send marketing email unless you opt in.</p>
            <p>To fix bugs — error logs help us diagnose problems. They're kept for 30 days and then deleted.</p>
          </Section>

          <Section title="Who we share it with">
            <p>We use a small number of third-party services to run Taskoru:</p>
            <ul className="ml-4 list-disc space-y-1">
              <li><strong className="text-foreground">Cloudflare</strong> — hosting, database, and file storage. Your data lives on Cloudflare's infrastructure.</li>
              <li><strong className="text-foreground">Stripe</strong> — payment processing for Pro and Business plans.</li>
              <li><strong className="text-foreground">Google</strong> — only if you choose to sign in with Google. We receive your name and email from Google and nothing else.</li>
            </ul>
            <p>We don't sell your data. We don't share it with anyone else.</p>
          </Section>

          <Section title="File attachments">
            <p>Files attached to cards are stored in Cloudflare R2 object storage. They're accessible only via signed URLs tied to your account. We don't scan or process the contents of your files.</p>
          </Section>

          <Section title="Cookies and sessions">
            <p>We use a single session cookie to keep you logged in. No tracking cookies, no third-party analytics cookies. If you clear your cookies you'll be logged out.</p>
          </Section>

          <Section title="Your rights">
            <p>You can export or delete your data at any time. To delete your account and all associated data, contact us at the address below and we'll take care of it within 7 days.</p>
            <p>If you're in the EU, you have additional rights under GDPR including the right to access, correct, or port your data. Reach out and we'll help.</p>
          </Section>

          <Section title="Data retention">
            <p>Your data is kept for as long as your account is active. If you delete your account, we remove your personal data within 30 days. Backups are purged within 90 days.</p>
          </Section>

          <Section title="Security">
            <p>Passwords are hashed using bcrypt. All traffic is encrypted over HTTPS. We don't store payment card details. We take security seriously and will notify you promptly if there's ever a breach that affects your account.</p>
          </Section>

          <Section title="Children">
            <p>Taskoru is not directed at children under 13. We don't knowingly collect data from anyone under 13.</p>
          </Section>

          <Section title="Changes">
            <p>If we make material changes to this policy we'll update the date at the top and notify users by email. Continued use after changes means you accept the new terms.</p>
          </Section>

          <Section title="Contact">
            <p>Questions? Reach us through the <a href="/landing#contact" className="text-primary hover:underline">contact form</a> on our site or email us directly. We'll get back to you.</p>
          </Section>
        </div>
      </main>

      <footer className="border-t bg-card px-4 py-8 sm:px-6">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="Taskoru" className="h-7 w-7 rounded-xl" />
            <span className="font-semibold">Taskoru</span>
          </div>
          <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} Taskoru. All rights reserved.</p>
          <div className="flex gap-4 text-sm text-muted-foreground">
            <a href="/landing#contact" className="hover:text-foreground">Contact</a>
            <Link to="/privacy" className="text-foreground font-medium">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
